import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { getAllResultBreakdowns, type ResultBreakdown } from "../../../service/result-breakdown"
import {
  listBreakdownSubjects,
  saveMarksGrid,
  getMarksGrid,
  getFinalTermPrep,
  recomputeFinalTerm,
  type BreakdownSubject,
  type FinalTermPrep,
  type FinalTermRow,
  type FinalTermPct,
} from "../../../service/marks-entry"

const FinalTermEntry = () => {
  const [breakdowns, setBreakdowns] = useState<ResultBreakdown[]>([])
  const [breakdownId, setBreakdownId] = useState("")
  const [subjects, setSubjects] = useState<BreakdownSubject[]>([])
  const [selectedCs, setSelectedCs] = useState("")
  const [prep, setPrep] = useState<FinalTermPrep | null>(null)
  const [loadingPrep, setLoadingPrep] = useState(false)

  // Each breakdown already encodes session + class (e.g. "Final Term Examination
  // (Grade 2) 2025-2026"), so — like RMS — there's no separate Session/Class
  // picker. Pick a breakdown, then a course structure (class + subject).
  useEffect(() => {
    getAllResultBreakdowns({ limit: 300 })
      .then((r: any) => {
        const list = r?.data?.data || r?.data || []
        // Numeric-aware sort so "Grade 2" precedes "Grade 10".
        list.sort((a: any, b: any) =>
          String(a.title || a.name || "").localeCompare(String(b.title || b.name || ""), undefined, { numeric: true })
        )
        setBreakdowns(list)
      })
      .catch(() => setBreakdowns([]))
  }, [])

  // On breakdown change → list its course structures (class + subject).
  useEffect(() => {
    setSelectedCs("")
    setPrep(null)
    if (!breakdownId) {
      setSubjects([])
      return
    }
    listBreakdownSubjects(breakdownId)
      .then((r) => setSubjects(r?.data || []))
      .catch(() => setSubjects([]))
  }, [breakdownId])

  const loadPrep = async (csId: string) => {
    if (!csId || !breakdownId) return
    setSelectedCs(csId)
    setLoadingPrep(true)
    try {
      const r = await getFinalTermPrep({ courseStructureId: csId, breakdownId })
      setPrep(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load final term data")
      setPrep(null)
    } finally {
      setLoadingPrep(false)
    }
  }

  const isLocked = prep
    ? prep.breakdown.status === "Published" || prep.breakdown.status === "Locked"
    : false

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Final Term Entry (Carryover)</h3>
            <p className="text-muted mb-0">
              Final Term grade = weighted blend of all term marks. Prior terms are read-only; enter Final
              Exam marks below.
            </p>
          </div>
        </div>

        {/* Pickers — the breakdown already pins session + class (RMS-style),
            so we only need Breakdown → Course structure (class + subject). */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Breakdown (Final)</label>
                <select
                  className="form-select"
                  value={breakdownId}
                  onChange={(e) => setBreakdownId(e.target.value)}
                >
                  <option value="">— Select breakdown —</option>
                  {breakdowns.map((b: any) => (
                    <option key={b._id} value={b._id}>
                      {b.title || b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Course structure (class + subject)</label>
                <select
                  className="form-select"
                  value={selectedCs}
                  onChange={(e) => e.target.value && loadPrep(e.target.value)}
                  disabled={!breakdownId || subjects.length === 0}
                >
                  <option value="">
                    {breakdownId
                      ? subjects.length
                        ? "— Select class + subject —"
                        : "No course structures for this breakdown"
                      : "— Select a breakdown first —"}
                  </option>
                  {subjects.map((s) => (
                    <option key={s.courseStructureId} value={s.courseStructureId}>
                      {s.label} (/{s.totalMarks})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Final term grid */}
        {loadingPrep && <p className="text-muted">Loading…</p>}
        {prep && !loadingPrep && (
          <FinalTermGrid
            prep={prep}
            breakdownId={breakdownId}
            isLocked={isLocked}
            onRecomputed={() => loadPrep(selectedCs)}
          />
        )}
      </div>
    </div>
  )
}

const computeCombined = (row: FinalTermRow, termPcts: FinalTermPct[], finalEntry: string): number => {
  let total = 0
  for (const tp of termPcts) {
    let val: number
    if (tp.isFinal) {
      const n = parseFloat(finalEntry)
      val = Number.isFinite(n) ? n : 0
    } else {
      const prior = row.prior[tp.termId]
      val = typeof prior === "number" ? prior : 0
    }
    total += (val * tp.percentage) / 100
  }
  return total
}

const FinalTermGrid = ({
  prep,
  breakdownId,
  isLocked,
  onRecomputed,
}: {
  prep: FinalTermPrep
  breakdownId: string
  isLocked: boolean
  onRecomputed: () => void
}) => {
  const [entries, setEntries] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const priorPcts = prep.term_percentages.filter((t) => !t.isFinal)
  const finalPct = prep.term_percentages.find((t) => t.isFinal)

  // Mirrors RMS "Save & recompute": save the Final Exam marks (if any changed),
  // then recompute + PERSIST the weighted combined grade for every student.
  const save = async () => {
    setSaving(true)
    try {
      const entriesToSave = Object.entries(entries)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([enrollmentId, v]) => ({
          enrollmentId,
          subCategory: null,
          obtainedMarks: parseFloat(v),
          isAbsent: false,
          isNa: false,
        }))

      if (entriesToSave.length > 0) {
        // Find-or-create the award slip for this CS to get its awardSlipId.
        const gridRes = await getMarksGrid({
          session: "",
          class: "",
          section: "",
          breakdown: breakdownId,
          courseStructureId: prep.course_structure.id,
        })
        const awardSlipId = gridRes?.data?.awardSlipId
        if (!awardSlipId) throw new Error("Could not find award slip")
        await saveMarksGrid({ awardSlipId, entries: entriesToSave })
      }

      // Persist the carryover blend for everyone (RMS final-term/recompute).
      const rc = await recomputeFinalTerm({
        courseStructureId: prep.course_structure.id,
        breakdownId,
      })
      toast.success(`Saved & recomputed ${rc.data.updated} student(s)`)
      setEntries({})
      onRecomputed()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-0">{prep.course_structure.subject_name}</h5>
          <small className="text-muted">{prep.rows.length} students</small>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={saving || isLocked || prep.rows.length === 0}
        >
          {saving ? "Saving…" : "Save & Recompute"}
        </button>
      </div>

      {/* Term weights banner */}
      {prep.term_percentages.length > 0 && (
        <div className="alert alert-info m-3 py-2 mb-0">
          <strong>Term weights: </strong>
          {prep.term_percentages.map((t) => (
            <span key={t.termId} className="me-3">
              {t.termName}: <strong>{t.percentage}%</strong>
              {t.isFinal && <span className="badge bg-primary ms-1">Final</span>}
            </span>
          ))}
          {prep.term_percentages.length === 0 && (
            <span className="text-warning">
              No term percentages configured. Add them to the Course Structure first.
            </span>
          )}
        </div>
      )}

      {isLocked && (
        <div className="alert alert-warning mx-3 mt-3 mb-0 py-2">
          This breakdown is {prep.breakdown.status} — marks are locked.
        </div>
      )}

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-bordered table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Name</th>
                {priorPcts.map((t) => (
                  <th key={t.termId} className="bg-light">
                    {t.termName}
                    <small className="d-block text-muted">({t.percentage}%)</small>
                  </th>
                ))}
                <th>
                  Final Exam
                  {finalPct && (
                    <small className="d-block text-muted">({finalPct.percentage}%)</small>
                  )}
                </th>
                <th>Combined <small className="d-block text-muted">(live)</small></th>
                <th>Stored <small className="d-block text-muted">(saved)</small></th>
              </tr>
            </thead>
            <tbody>
              {prep.rows.map((row, i) => {
                const finalEntered = entries[row.student.id] ?? (row.final_exam_total != null ? String(row.final_exam_total) : "")
                const combined = computeCombined(row, prep.term_percentages, finalEntered)
                return (
                  <tr key={row.student.id}>
                    <td className="text-muted small">{i + 1}</td>
                    <td>
                      <div className="fw-semibold small">{row.student.full_name}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Roll: {row.student.roll_number}
                      </div>
                    </td>
                    {priorPcts.map((t) => {
                      const val = row.prior[t.termId]
                      return (
                        <td key={t.termId} className="bg-light text-center">
                          {val == null ? (
                            <span className="text-warning small">missing</span>
                          ) : val === "AB" ? (
                            <span className="badge bg-warning text-dark">AB</span>
                          ) : val === "NA" ? (
                            <span className="badge bg-secondary">NA</span>
                          ) : (
                            <span>{typeof val === "number" ? val.toFixed(2) : val}</span>
                          )}
                        </td>
                      )
                    })}
                    <td className="p-1">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={prep.course_structure.total_marks}
                        disabled={isLocked}
                        value={finalEntered}
                        onChange={(e) =>
                          setEntries((s) => ({ ...s, [row.student.id]: e.target.value }))
                        }
                        className="form-control form-control-sm"
                        style={{ width: 90 }}
                      />
                    </td>
                    <td className="fw-semibold text-center">{combined.toFixed(2)}</td>
                    <td className="text-center">
                      {row.stored ? (
                        (() => {
                          const stored = row.stored.combined_final_term
                          const drift = Math.abs(stored - combined) > 0.01
                          return (
                            <span
                              className={drift ? "text-warning fw-semibold" : "text-success fw-semibold"}
                              title={drift ? "Live value differs from saved — click Save & Recompute" : "Saved"}
                            >
                              {stored.toFixed(2)}
                              {drift && <i className="ti ti-alert-triangle ms-1" />}
                            </span>
                          )
                        })()
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
              {prep.rows.length === 0 && (
                <tr>
                  <td colSpan={5 + priorPcts.length} className="text-center text-muted py-4">
                    No students enrolled
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {prep.subcategories.length > 0 && (
          <p className="text-muted small mx-3 mt-2">
            Note: This subject has sub-categories. Use the standard Marks Entry screen to enter
            per-subcategory final exam marks.
          </p>
        )}
      </div>
    </div>
  )
}

export default FinalTermEntry
